import type { IconDefinition } from "@fullscript/fontawesome-common-types";
import type { ReactNode } from "react";

import { Tooltip } from "@aviary";
import type { PopperPositionType } from "@aviary/types/popperPositions";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import * as styles from "./IconWrapper.styles";

type IconVariation = keyof typeof styles.icon.variations;

interface Props {
  icon: IconDefinition;
  iconColor?: string;
  iconPosition?:
    | "top"
    | "right"
    | "bottom"
    | "left"
    | "topLeft"
    | "topRight"
    | "bottomLeft"
    | "bottomRight";
  tooltipMessage?: ReactNode;
  tooltipPosition?: PopperPositionType;
  tooltipTextDirection?: "left" | "right";
  wrapperAlignment?: "start" | "end" | "center";
  children: ReactNode;
}

const IconWrapper = ({
  children,
  icon,
  tooltipMessage,
  iconColor,
  iconPosition = "right",
  tooltipPosition = "left",
  tooltipTextDirection,
  wrapperAlignment = "center",
}: Props) => {
  const conditionalWrapperStyles = [
    styles.iconWrapper.base,
    wrapperAlignment && styles.iconWrapper.wrapperAlignment[wrapperAlignment],
  ];

  const iconVariation =
    iconColor && iconColor in styles.icon.variations
      ? styles.icon.variations[iconColor as IconVariation]
      : undefined;
  const conditionalIconStyles = [
    styles.icon.base,
    styles.icon.iconPosition[iconPosition],
    Boolean(tooltipMessage) && styles.icon.hasTooltip,
    iconVariation,
  ];

  const renderIcon = () => {
    if (tooltipMessage) {
      return (
        <Tooltip
          tooltipContent={tooltipMessage}
          textDirection={tooltipTextDirection}
          placement={tooltipPosition}
          css={styles.tooltip}
        >
          <FontAwesomeIcon
            className="legacy-iconWrapper__icon"
            css={conditionalIconStyles}
            icon={icon}
          />
        </Tooltip>
      );
    }

    return (
      <FontAwesomeIcon
        className="legacy-iconWrapper__icon"
        css={conditionalIconStyles}
        icon={icon}
      />
    );
  };

  return (
    <div
      className="legacy-iconWrapper__wrapper"
      css={conditionalWrapperStyles}
      data-testid="icon-wrapper"
    >
      {children}
      {renderIcon()}
    </div>
  );
};

export { IconWrapper };
